import { APITypes } from '@streem/api';
import { action, autorun, computed, IReactionDisposer, observable } from 'mobx';
import { streem } from 'streem-sdk-protobuf';
import { CompanySettingsStore } from './company_settings_store';
import { SDKStore } from './sdk_store';
import appLogger from '../util/logging/app_logger';
import type { SettingsDictionary } from '../util/company_settings';

const GroupReservationStatus = streem.api.GroupReservationStatus;

export class CurrentCallSourceStore {
    constructor(
        private readonly sdkStore: SDKStore,
        private readonly companySettingsStore: CompanySettingsStore,
    ) {}

    @observable
    private _groupReservationCallSource?: APITypes.StreemApiCallSource | undefined;

    @computed
    public get groupReservationCallSource(): APITypes.StreemApiCallSource | undefined {
        if (!this._groupReservationCallSource) {
            appLogger.info(`No group reservation call source details exist, returning undefined`);
            return;
        }

        appLogger.info(
            `Getting call source details for reservation: ${
                this.sdkStore.groupReservation?.current?.reservationSid
            }, ${JSON.stringify(this._groupReservationCallSource)}`,
        );
        return this._groupReservationCallSource;
    }

    @action
    public setGroupReservationCallSource(
        companyCallSources: Readonly<Array<APITypes.StreemApiCallSource>>,
    ): void {
        const currentGroupReservation = this.sdkStore.groupReservation.current;
        if (companyCallSources && currentGroupReservation && currentGroupReservation.callDetail) {
            this._groupReservationCallSource = companyCallSources.find(
                callSource => callSource.sid === currentGroupReservation?.callDetail?.callSourceSid,
            );

            appLogger.info(
                `Call source details have been set for reservation: ${
                    currentGroupReservation.reservationSid
                }, ${JSON.stringify(this._groupReservationCallSource)}`,
            );

            if (this._groupReservationCallSource == null) {
                appLogger.warn(
                    `Call source not found for callSourceId: ${this.sdkStore.groupReservation.current?.callDetail?.callSourceSid} for reservation: ${this.sdkStore.groupReservation.current?.id}`,
                );
            }
        }
    }

    @computed
    private get settingsForCurrentSource(): SettingsDictionary {
        const code = this._groupReservationCallSource?.code;
        if (!code) {
            return {};
        }

        const settings = this.companySettingsStore.sourceSettingsDictionary[code];
        if (!settings) {
            return {};
        }
        return settings;
    }

    @computed
    public get callRequeueEnabledForCurrentSource(): boolean {
        const settings = this.settingsForCurrentSource;
        const setting = settings['CALL_REQUEUE_ENABLED'];
        return setting != null && setting.value === 'true';
    }

    @action
    public clearCallSourceDetailsOnCancelledOrExpiredOrDeclined(): IReactionDisposer {
        return autorun(() => {
            if (this.sdkStore.groupReservation?.current) {
                const reservationCanceledOrExpiredOrDeclined = [
                    GroupReservationStatus.GROUP_RESERVATION_STATUS_CANCELED,
                    GroupReservationStatus.GROUP_RESERVATION_STATUS_EXPIRED,
                    // Failed status can be reached when expert declines call
                    GroupReservationStatus.GROUP_RESERVATION_STATUS_FAILED,
                ].includes(this.sdkStore.groupReservation.current.reservationStatus);

                if (reservationCanceledOrExpiredOrDeclined && this._groupReservationCallSource) {
                    appLogger.info(
                        `Reservation: ${this.sdkStore.groupReservation.current.reservationSid} expired, was cancelled, or declined, clearing call details`,
                    );
                    this.clearGroupReservationCallSource();
                }
            }
        });
    }

    @action
    public clearGroupReservationCallSource(): void {
        // not all calls are reservation calls
        if (!this.sdkStore.groupReservation?.current) return;
        appLogger.info(
            `Clearing group reservation call source for reservationId: ${this.sdkStore.groupReservation?.current?.reservationSid}`,
        );
        this._groupReservationCallSource = undefined;
    }
}
